<script setup lang="ts">
import CompanyCreateDialog from '@/components/companies/CompanyCreateDialog.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCustomPositionListCard from '@/components/FinestCustomPositionListCard.vue'
import type { PositionValue, PredefinedPosition } from '@/components/finest-custom-position-list/types'
import ShareholderPersonDialog from '@/components/companies/ShareholderPersonDialog.vue'
import { idOf, usePersistedRows } from '@/composables/usePersistedRows'
import { useToastMessages } from '@/composables/useToastMessages'
import { requestJson } from '@/utils/postJson'
import CompanyShareholdingController from '@controllers/Investors/CompanyShareholdingController'
import { ulid } from 'ulid'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CompanyData = App.Data.Company.CompanyData
import NaturalPersonData = App.Data.User.NaturalPersonData
import ToastData = App.Data.ToastData
import ShareholderType = App.Enums.Company.ShareholderType

const props = defineProps<{
  company: CompanyData
  availableNaturalPersons: NaturalPersonData[]
  availableCompanies: CompanyData[]
  existingShareholdings: string[]
}>()

const { t, locale } = useI18n()

const nameById = computed(() => {
  const map = new Map<string, string>()
  props.availableNaturalPersons.forEach((person) => map.set(person.id as string, person.name as string))
  props.availableCompanies.forEach((company) => map.set(company.id as string, company.name as string))

  return map
})

const companyIds = computed(() => new Set(props.availableCompanies.map((company) => company.id as string)))

const shareholderTypeFor = (id: string): ShareholderType =>
  (companyIds.value.has(id) ? 'company' : 'natural-person') as ShareholderType

type NewPerson = { first_name: string; last_name: string }

type ShareholderRow = PositionValue & { newPerson?: NewPerson }

type SharePayload = { percentage: number | null }

const toPayload = (row: ShareholderRow): SharePayload => ({ percentage: row.value })

const identityOf = (row: ShareholderRow) =>
  row.newPerson
    ? { new_person: row.newPerson }
    : { shareholder_type: shareholderTypeFor(row.key), shareholder_id: row.key }

const hasValidShare = (row: ShareholderRow): boolean => row.value !== null && row.value > 0 && row.value <= 100

const tableKeyOf = (row: ShareholderRow): string => row.id ?? row.key

const showToastMessages = useToastMessages()

const companyId = (): string => props.company.id ?? ''

const shareholdingRoute = (shareholderId: string): [string, string] => [companyId(), shareholderId]

const {
  rows: positions,
  reset,
  addDraft,
  removeRow,
  persistOnLeave,
} = usePersistedRows<ShareholderRow, SharePayload>({
  toPayload,
  isDraft: (row) => !hasValidShare(row),
  create: async (payload, key): Promise<string> => {
    const row = positions.value.find((candidate: ShareholderRow) => candidate.key === key)

    if (!row) {
      throw new Error(`The shareholder row ${key} is gone.`)
    }

    return idOf(
      await requestJson<{ id: string }>('post', CompanyShareholdingController.store(companyId()).url, {
        ...identityOf(row),
        ...payload,
      }),
    )
  },
  update: (id, payload) => requestJson('put', CompanyShareholdingController.update(shareholdingRoute(id)).url, payload),
  destroy: async (id) => {
    const response = await requestJson<{ toasts?: ToastData[] } | null>(
      'delete',
      CompanyShareholdingController.destroy(shareholdingRoute(id)).url,
      undefined,
    )

    showToastMessages(response?.toasts ?? [])
  },
  reloadProps: ['company', 'availableNaturalPersons', 'existingShareholdings', 'fieldStates', 'companyCompletion'],
  canSave: (): boolean => !invalidAllocation.value,
  saveOrder: (row, saved) => (row.value ?? 0) - (saved?.percentage ?? 0),
})

const serverPositions = (): ShareholderRow[] =>
  (props.company.upstreamShareholdings ?? [])
    .filter((shareholding) => shareholding.is_shareholder)
    .map((shareholding) => ({
      key: shareholding.shareholder_id as string,
      id: shareholding.shareholder_id as string,
      label: nameById.value.get(shareholding.shareholder_id as string) ?? '',
      value: shareholding.percentage ?? null,
      custom: false,
    }))

reset(serverPositions())

watch(
  () => props.company,
  () => reset(serverPositions()),
)

const sortByLabel = (options: PredefinedPosition[]): PredefinedPosition[] =>
  [...options].sort((a, b) => a.label.localeCompare(b.label, locale.value))

const hasName = (person: NaturalPersonData): boolean => (person.name ?? '').trim().length > 0

const shareholderKeys = computed<PredefinedPosition[]>(() => {
  const activeIds = new Set(positions.value.map(tableKeyOf))

  const persons = props.availableNaturalPersons.filter(hasName).map((person) => ({
    key: person.id as string,
    label: person.name as string,
    group: t('groups.persons'),
    icon: 'pi pi-user',
  }))

  const companies = props.availableCompanies
    .filter(
      (company) => activeIds.has(company.id as string) || !props.existingShareholdings.includes(company.id as string),
    )
    .map((company) => ({
      key: company.id as string,
      label: company.name as string,
      group: t('groups.companies'),
      icon: 'pi pi-building',
    }))

  return [...sortByLabel(persons), ...sortByLabel(companies)]
})

const total = computed<number>(() => positions.value.reduce((sum, position) => sum + (position.value ?? 0), 0))
const invalidAllocation = computed<boolean>(() => total.value > 100)

const knownShareholderIds = computed(() => new Set(shareholderKeys.value.map((shareholder) => shareholder.key)))

const tableValues = computed<PositionValue[]>(() =>
  positions.value.map((row) => ({
    key: tableKeyOf(row),
    label: row.label,
    value: row.value,
    custom: !knownShareholderIds.value.has(tableKeyOf(row)),
  })),
)

const applyTableChange = (next: PositionValue[]): void => {
  const nextByKey = new Map(next.map((entry) => [entry.key, entry]))
  const shownKeys = new Set(positions.value.map(tableKeyOf))

  positions.value = [
    ...positions.value.map((row) => {
      const changed = nextByKey.get(tableKeyOf(row))

      return changed ? { ...row, value: changed.value } : row
    }),
    ...next.filter((entry) => !shownKeys.has(entry.key)).map((entry) => ({ ...entry, id: null, custom: false })),
  ]

  positions.value.filter((row) => !nextByKey.has(tableKeyOf(row))).forEach((row) => removeRow(row.key))
}

const showCompanyDialog = ref(false)

const onCompanyCreated = (createdCompanyId: string) =>
  addDraft({
    key: createdCompanyId,
    id: null,
    label: nameById.value.get(createdCompanyId) ?? '',
    value: null,
    custom: false,
  })

const showPersonDialog = ref(false)

const onPersonCreated = (person: NewPerson): void =>
  addDraft({
    key: ulid(),
    id: null,
    label: [person.first_name, person.last_name].filter(Boolean).join(' '),
    value: null,
    custom: true,
    newPerson: person,
  })
</script>

<template>
  <div @focusout="persistOnLeave">
    <FinestCustomPositionListCard
      :model-value="tableValues"
      :title="t('title')"
      :position-header="t('columns.name')"
      :value-header="t('columns.share')"
      :keys="shareholderKeys"
      value-addon-label="%"
      :min="0"
      :max="100"
      :min-fraction-digits="0"
      :max-fraction-digits="2"
      @update:model-value="applyTableChange">
      <template #actions>
        <div class="flex flex-wrap gap-2">
          <FinestButton
            :label="t('add.person')"
            icon="pi pi-plus"
            variant="secondary"
            size="sm"
            @click="showPersonDialog = true" />
          <FinestButton
            :label="t('add.company')"
            icon="pi pi-plus"
            variant="secondary"
            size="sm"
            @click="showCompanyDialog = true" />
        </div>
      </template>
    </FinestCustomPositionListCard>
  </div>

  <Message v-if="invalidAllocation" severity="warn" :closable="false" class="mt-4">
    {{ t('invalidAllocation') }}
  </Message>

  <CompanyCreateDialog
    v-model:visible="showCompanyDialog"
    :show-trigger="false"
    return-to="estate_object_owner"
    @created="onCompanyCreated" />

  <ShareholderPersonDialog v-model:visible="showPersonDialog" @created="onPersonCreated" />
</template>

<i18n>
{
  "de": {
    "title": "Gesellschafter",
    "columns": {
      "name": "Name / Firma",
      "share": "Anteil %"
    },
    "groups": {
      "persons": "Natürliche Personen",
      "companies": "Gesellschaften"
    },
    "add": {
      "person": "Neue Person hinzufügen",
      "company": "Neue Gesellschaft anlegen"
    },
    "invalidAllocation": "Die Summe der Anteile darf 100 % nicht überschreiten."
  },
  "en": {
    "title": "Shareholders",
    "columns": {
      "name": "Name / Company",
      "share": "Share %"
    },
    "groups": {
      "persons": "Natural persons",
      "companies": "Companies"
    },
    "add": {
      "person": "Add new person",
      "company": "Add new company"
    },
    "invalidAllocation": "The sum of shares must not exceed 100%."
  }
}
</i18n>
